// ── NewsList.js ────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import api from '../../utils/api';
import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import './News.css';

const IMG_BASE = process.env.REACT_APP_IMG_URL || 'http://localhost:5000';

export function NewsList() {
  const [news,       setNews]       = useState([]);
  const [categories, setCategories] = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [pages,      setPages]      = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [category,   setCategory]   = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 9 });
    if (search)   params.set('search', search);
    if (category) params.set('category', category);
    api.get(`/news?${params}`)
      .then(r => { setNews(r.data.data); setTotal(r.data.total); setPages(r.data.pages); })
      .finally(() => setLoading(false));
  }, [page, search, category]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/news-categories').then(r => setCategories(r.data));
  }, []);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); load(); };

  return (
    <>
      <Header />
      <main className="news-page">
        <div className="news-page-hero">
          <div className="container">
            <h1 className="page-title" style={{color:'#fff'}}>Lajmet</h1>
            <p style={{color:'#b0c9e8',marginTop:6}}>Ndiqni lajmet dhe njoftime të fundit nga Spitali i Prizrenit</p>
          </div>
        </div>

        <div className="container news-layout">
          <div className="news-main">
            {/* Search + Filter */}
            <div className="news-toolbar">
              <form className="search-form" onSubmit={handleSearch}>
                <Search size={16} className="search-icon" />
                <input
                  type="text" placeholder="Kërko lajme..."
                  className="search-input"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </form>
              <div className="cat-filters">
                <button
                  className={`cat-btn ${!category ? 'active' : ''}`}
                  onClick={() => { setCategory(''); setPage(1); }}
                >Të gjitha</button>
                {categories.map(c => (
                  <button
                    key={c.id}
                    className={`cat-btn ${category === c.slug ? 'active' : ''}`}
                    style={category === c.slug ? { background: c.color, borderColor: c.color } : {}}
                    onClick={() => { setCategory(c.slug); setPage(1); }}
                  >{c.name}</button>
                ))}
              </div>
            </div>

            <p className="result-count">{total} lajme gjithsej</p>

            {loading ? <div className="spinner" /> : (
              <>
                <div className="news-grid-pub">
                  {news.map(item => (
                    <Link key={item.id} to={`/lajmet/${item.slug}`} className="news-pub-card">
                      <div className="news-pub-img">
                        {item.cover_image
                          ? <img src={`${IMG_BASE}${item.cover_image}`} alt={item.title} />
                          : <div className="news-pub-img-ph">🏥</div>
                        }
                        {item.category_name && (
                          <span className="news-cat-badge" style={{background: item.category_color}}>
                            {item.category_name}
                          </span>
                        )}
                      </div>
                      <div className="news-pub-body">
                        <p className="news-pub-date">
                          {item.published_at ? new Date(item.published_at).toLocaleDateString('sq-AL') : ''}
                        </p>
                        <h2 className="news-pub-title">{item.title}</h2>
                        {item.excerpt && <p className="news-pub-excerpt">{item.excerpt}</p>}
                        <span className="news-pub-more">Lexo më shumë →</span>
                      </div>
                    </Link>
                  ))}
                </div>

                {pages > 1 && (
                  <div className="pagination">
                    {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                      <button key={p} className={`page-btn ${p === page ? 'active' : ''}`}
                        onClick={() => setPage(p)}>{p}</button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

// ── NewsDetail.js ──────────────────────────────────────────
export function NewsDetail() {
  const slug = window.location.pathname.split('/').pop();
  const [item, setItem]   = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/news/${slug}`)
      .then(r => setItem(r.data))
      .catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <>
      <Header />
      <div className="container" style={{padding:'3rem 20px', textAlign:'center'}}>
        <h2>Lajmi nuk u gjet.</h2>
        <Link to="/lajmet" className="btn btn-secondary" style={{marginTop:16}}>← Kthehu</Link>
      </div>
      <Footer />
    </>
  );

  if (!item) return <><Header /><div className="spinner"/><Footer /></>;

  return (
    <>
      <Header />
      <div className="container news-detail-wrap">
        <Link to="/lajmet" className="back-link">← Kthehu tek lajmet</Link>
        <article className="news-detail">
          {item.cover_image && (
            <img src={`${IMG_BASE}${item.cover_image}`} alt={item.title} className="detail-cover" />
          )}
          <div className="detail-meta">
            {item.category_name && (
              <span className="badge" style={{background: item.category_color, color:'#fff'}}>
                {item.category_name}
              </span>
            )}
            <span className="detail-date">
              {item.published_at ? new Date(item.published_at).toLocaleDateString('sq-AL', {
                day: 'numeric', month: 'long', year: 'numeric'
              }) : ''}
            </span>
            <span className="detail-author">nga {item.author_name}</span>
          </div>
          <h1 className="detail-title">{item.title}</h1>
          {item.excerpt && <p className="detail-excerpt">{item.excerpt}</p>}
          <div
            className="rich-content detail-content"
            dangerouslySetInnerHTML={{ __html: item.content }}
          />
        </article>
      </div>
      <Footer />
    </>
  );
}
